import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { doc, getDoc, collection, addDoc, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  Button,
  Row,
  Col,
  Card,
  Form,
  ProgressBar,
  Container,
  Alert,
  FormControl,
} from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Header from "../layouts/Header";

export default function YearlyDocuments() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("");
  const [permissions, setPermissions] = useState(null);
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [createdForEmail, setCreatedForEmail] = useState("");
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchUserData = async (authUser) => {
    if (authUser) {
      const userDocRef = doc(db, "Users", authUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setRole(userData.role || "user");
        return userData.role;
      } else {
        console.error("User document does not exist.");
        return null;
      }
    }
    return null;
  };

  const fetchPermissions = async (role) => {
    try {
      if (role === "admin") {
        setPermissions({ canViewYearlyDocuments: true });
        return;
      }

      const roleDocRef = doc(db, "Roles", role);
      const roleDoc = await getDoc(roleDocRef);

      if (roleDoc.exists()) {
        setPermissions(roleDoc.data().permissions);
      } else {
        console.error(`Role document does not exist for role: ${role}`);
        setPermissions(null);
      }
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setPermissions(null);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const userList = usersSnapshot.docs.map((doc) => ({
        email: doc.data().email,
        fullName: doc.data().fullName || "Unknown User",
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file || !createdForEmail || !uploadYear) {
      alert("Please complete all fields.");
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "documents");

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://api.cloudinary.com/v1_1/your-cloud-name/upload");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      };

      xhr.onload = async () => {
        const data = JSON.parse(xhr.responseText);
        const fileRef = collection(db, "YearlyDocuments");
        await addDoc(fileRef, {
          fileName: file.name,
          url: data.secure_url,
          size: file.size,
          type: file.type,
          year: uploadYear,
          uploadedAt: new Date(),
          createdBy: { email: user.email, fullName: user.displayName || "Unknown" },
          createdFor: createdForEmail,
        });

        setUploading(false);
        setUploadProgress(0);
        setFile(null);
        setCreatedForEmail("");
        alert("Document uploaded successfully!");
        fetchFiles();
      };

      xhr.onerror = () => {
        setUploading(false);
        setUploadProgress(0);
        alert("Failed to upload document.");
      };

      xhr.send(formData);
    } catch (err) {
      console.error(err);
      alert("Failed to upload document.");
    }
  };

  const fetchFiles = async () => {
    try {
      const fileSnapshot = await getDocs(collection(db, "YearlyDocuments"));
      const allFiles = fileSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      let filteredFiles = [];
      if (role === "admin") {
        filteredFiles = selectedUser
          ? allFiles.filter(
              (file) =>
                file.createdBy.email === selectedUser.email ||
                file.createdFor === selectedUser.email
            )
          : allFiles;
      } else {
        filteredFiles = allFiles.filter(
          (file) => file.createdBy.email === user.email || file.createdFor === user.email
        );
      }

      setFiles(filteredFiles);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch files.");
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowerCaseQuery = query.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(lowerCaseQuery) ||
        user.email.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        const userRole = await fetchUserData(authUser);
        if (userRole) await fetchPermissions(userRole);
      } else {
        setUser(null);
        setRole("");
        setPermissions(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (permissions?.canViewYearlyDocuments || role === "admin") {
      fetchFiles();
    }
  }, [permissions, role, selectedUser]);

  useEffect(() => {
    if (role === "admin") {
      fetchAllUsers();
    }
  }, [role]);

  if (!permissions?.canViewYearlyDocuments && role !== "admin") {

    console.log("permissions" + permissions);
    return (
      <Container>
        <Alert variant="danger" className="text-center mt-5">
          Loading Content or Access Denied..
        </Alert>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-booklet-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Yearly Documents</h1>
          </div>
          <p className="lead text-muted">Manage and access annual documents and reports</p>
        </div>

        {/* Enhanced Alert Card */}
        <div className="bg-gradient-primary text-white rounded-4 p-4 shadow-lg mb-5"
             style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <div className="d-flex align-items-center">
            <i className="ri-information-line fs-4 me-3"></i>
            <div>
              <h6 className="mb-1 fw-bold">Access Level</h6>
              <p className="mb-0 small">
                {role === "admin"
                  ? "You are logged in as an admin and can manage documents for all employees."
                  : `You are logged in as an employee. You can view your documents.`}
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Employee Filter (Admin Only) */}
        {role === "admin" && (
          <Card className="border-0 shadow-lg rounded-4 mb-5">
            <div className="bg-gradient-info text-white p-4 rounded-top-4" 
                 style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
              <div className="d-flex align-items-center">
                <i className="ri-user-search-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Filter Documents by Employee</h5>
              </div>
            </div>
            <Card.Body className="p-4">
              <FormControl
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="form-control-lg shadow-sm border-2 mb-3"
                style={{ borderRadius: '12px' }}
              />
              <PerfectScrollbar style={{ maxHeight: "200px", overflow: "auto" }}>
                <div className="list-group">
                  {filteredUsers.map((user, index) => (
                    <div
                      key={index}
                      className={`list-group-item list-group-item-action rounded-3 mb-2 border-0 ${
                        selectedUser === user ? "bg-primary text-white" : "bg-light"
                      }`}
                      onClick={() => setSelectedUser(user)}
                      style={{ 
                        cursor: "pointer",
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedUser !== user) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedUser !== user) {
                          e.currentTarget.style.backgroundColor = '';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div className={`rounded-circle p-2 me-3 ${
                          selectedUser === user ? 'bg-white bg-opacity-20' : 'bg-primary bg-opacity-10'
                        }`}>
                          <i className={`ri-user-line ${
                            selectedUser === user ? 'text-white' : 'text-primary'
                          }`}></i>
                        </div>
                        <div>
                          <h6 className={`mb-0 fw-semibold ${
                            selectedUser === user ? 'text-white' : 'text-dark'
                          }`}>
                            {user.fullName}
                          </h6>
                          <small className={`${
                            selectedUser === user ? 'text-white opacity-75' : 'text-muted'
                          }`}>
                            {user.email}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </PerfectScrollbar>
            </Card.Body>
          </Card>
        )}

        {/* Enhanced Upload Form (Admin Only) */}
        {role === "admin" && (
          <Card className="border-0 shadow-lg rounded-4 mb-5">
            <div className="bg-gradient-success text-white p-4 rounded-top-4" 
                 style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
              <div className="d-flex align-items-center">
                <i className="ri-upload-cloud-2-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Upload Document</h5>
              </div>
            </div>
            <Card.Body className="p-4">
              <Row className="g-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-file-line me-2"></i>Select Document
                    </Form.Label>
                    <Form.Control
                      type="file"
                      onChange={handleFileChange}
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                      placeholder="Select a document"
                    />
                    <Form.Text className="text-muted mt-2">
                      <i className="ri-information-line me-1"></i>
                      Supported formats: PDF, DOC, DOCX, XLS, XLSX
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="ri-mail-line me-2"></i>Recipient Email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={createdForEmail}
                      onChange={(e) => setCreatedForEmail(e.target.value)}
                      placeholder="Enter recipient's email"
                      className="form-control-lg shadow-sm border-2"
                      style={{ borderRadius: '12px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <div className="d-flex gap-3">
                    <Button 
                      variant="primary" 
                      onClick={handleUpload} 
                      disabled={uploading}
                      className="px-5 py-3 rounded-pill fw-semibold shadow-lg"
                    >
                      {uploading ? (
                        <>
                          <i className="ri-loader-4-line me-2"></i>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-2-line me-2"></i>
                          Upload Document
                        </>
                      )}
                    </Button>
                  </div>
                  {uploading && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Upload Progress</small>
                        <small className="text-muted fw-semibold">{uploadProgress}%</small>
                      </div>
                      <ProgressBar
                        animated
                        now={uploadProgress}
                        className="shadow-sm"
                        style={{ height: '8px', borderRadius: '10px' }}
                      />
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Enhanced Documents Section */}
        <Card className="border-0 shadow-lg rounded-4">
          <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-folder-open-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Accessible Documents</h5>
              </div>
              <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                {files.length} documents
              </span>
            </div>
          </div>
          <Card.Body className="p-4">
            {files.length > 0 ? (
              <PerfectScrollbar>
                <Row className="g-4">
                  {files.map((file) => (
                    <Col key={file.id} md={6} lg={4}>
                      <Card className="border-0 shadow-sm rounded-4 h-100"
                            style={{ 
                              transition: 'all 0.3s ease',
                              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-4px)';
                              e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                            }}>
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                              <i className="ri-file-line fs-4 text-primary"></i>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="fw-bold mb-0 text-truncate" title={file.fileName}>
                                {file.fileName}
                              </h6>
                              <small className="text-muted">Year: {file.year}</small>
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-muted small">Uploaded For:</span>
                              <span className="fw-semibold small text-truncate" title={file.createdFor}>
                                {file.createdFor}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-muted small">Uploaded At:</span>
                              <span className="fw-semibold small">
                                {new Date(file.uploadedAt.seconds * 1000).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between mb-3">
                              <span className="text-muted small">Size:</span>
                              <span className="fw-semibold small">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                          </div>

                          <div className="d-grid">
                            <a 
                              href={file.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-primary rounded-pill fw-semibold"
                            >
                              <i className="ri-download-line me-2"></i>
                              Download Document
                            </a>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </PerfectScrollbar>
            ) : (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ri-folder-open-line text-muted" style={{ fontSize: '4rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No Documents Found</h5>
                <p className="text-muted">
                  {role === "admin" 
                    ? "No documents have been uploaded yet or no documents match your current filter."
                    : "No documents have been shared with you yet."}
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </React.Fragment>
  );
}
